import React, {FC, useEffect, useState} from "react"
import moment from "moment"
import {Row} from "react-bootstrap"
import styled from "styled-components"

import Button, {IconButton} from "@irati/ui/src/Button"
import {Hr} from "@irati/ui/src/Layout"
import Flex from "@irati/ui/src/Flex"
import {Body, H3, Label} from "@irati/ui/src/Typography"
import {Checkbox, Input, Select} from "@irati/ui/src/FormItem"
import {IconTypes} from "@irati/ui/src/Icon"
import {IconTooltip} from "@irati/ui/src/Tooltip"
import Spacing from "@irati/ui/src/Spacing"
import {useViewOrderQuery, useUpdatePayrollOrder} from "@irati/payrollpay/hooks"
import Loading from "@irati/ui/src/Loading"
import FormattedMessage, {FormattedCurrency, useIntl} from "@irati/utils/lang"
import {PaymentCurrencyType, PayrollOrder} from "@irati/types"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import buttonTertiaryCss from "@irati/ui/src/Button/styled/tertiaryButtonCss"
import ToastAlert from "@irati/ui/src/ToastAlert"
import {triggerErrorToast, triggerSuccessToast} from "@irati/ui/src/ToastAlert/triggerToast"
import adminTracking from "@irati-tracking/admin"
import validation from "@irati/utils/validation"

import ProfilePicAndName from "../../components/OrderManagement/ProfilePicAndName"
import StatusPill from "../../components/OrderManagement/StatusPill"
import ViewOrderExpander from "../../components/OrderManagement/ViewOrderExpander"
import ViewOrderRow from "../../components/OrderManagement/ViewOrderRow"
import ApproveRejectButtonModals from "../../components/OrderManagement/ApproveRejectButtonModals"
import Comments from "./Comments"
import OtherOrders from "./OtherOrders"
import Documents from "./Documents"
import ApproveOrderModal from "./ApproveOrderModal"
import BackLink from "./BulkUpdateOrders/BackLink"

const FlexChild = styled.div`
  flex: 1;
`

const CommentSection = styled.div`
  width: 320px;
`

const NavigateThroughOrders = styled.div`
  display: flex;
  ${buttonTertiaryCss}
`

const PAID_BY_ANNUAL_SALARY = "admin.components.orderManagement.view.annual"
const PAID_BY_HOURLY_SALARY = "admin.components.orderManagement.view.hourly"

interface Props {
  order_number: string
  onBack: () => void
  orders: PayrollOrder[]
}

const OrderManagementView: FC<Props> = ({order_number, onBack, orders: originalOrders}) => {
  const [basic_details_check_complete, setBasicDetailsToggled] = useState(false)
  const [order_details_check_complete, setOrderDetailsToggled] = useState(false)
  const [national_minimum_wage_check_complete, setLimitationToggled] = useState(false)

  const [paidBy, setPaidBy] = useState(PAID_BY_ANNUAL_SALARY)
  const [salary, setSalary] = useState(0)
  const [hours, setHours] = useState(0)
  const [otherDeductions, setOtherDeductions] = useState(0)
  const [minimumWage, setMinimumWage] = useState(11.12)
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)
  const [showErrorAlert, setShowErrorAlert] = useState(false)

  const [employee_number, setEmployeeNumber] = useState(undefined)
  const [ni_number, setNINumber] = useState(undefined)
  const [apply_updates_to_employee_profile, setApplyUpdates] = useState(false)
  const [orders, setOrders] = useState(originalOrders)
  const [orderId, setOrderId] = useState(order_number)
  const [orderIndex, setOrderIndex] = useState(1)

  const [showApproveModal, setShowApproveModal] = useState(false)
  const {data, load, refetch} = useViewOrderQuery({order_number: orderId})
  const [mutate, updatePayrollOrderResponse] = useUpdatePayrollOrder()
  const {formatMessage} = useIntl()
  const readOnly = data?.approval_status !== "Pending" || data?.order_status === "Order cancelled"

  const monthsInYear = 12
  const weeksInYear = 52.143
  const monthlyGrossPayRevised =
    salary / monthsInYear - data?.amount_per_pay_period - (data?.amount_existing_orders || 0) - otherDeductions
  const monthlyEquivalentHourlyRate = (monthlyGrossPayRevised * monthsInYear) / weeksInYear / hours
  const hourlyAnnualSalary = salary * hours * weeksInYear
  const hourlyGrossPayRevised =
    hourlyAnnualSalary / monthsInYear - data?.amount_per_pay_period - (data?.amount_existing_orders || 0) - otherDeductions
  const hourlyquivalentHourlyRate = (hourlyGrossPayRevised * monthsInYear) / weeksInYear / hours

  useEffect(() => {
    if (data) {
      setEmployeeNumber(data?.employee_id)
      setNINumber(data?.ni_number)
      setBasicDetailsToggled(data?.basic_details_check_complete)
      setOrderDetailsToggled(data?.order_details_check_complete)
      setLimitationToggled(data?.national_minimum_wage_check_complete)
    }
  }, [data])

  useEffect(() => {
    if (originalOrders) {
      const index = originalOrders.findIndex(({id}) => id === order_number)
      const updatedOrders = [originalOrders[index], ...originalOrders.slice(0, index), ...originalOrders.slice(index + 1)]
      setOrders(updatedOrders)
    }
  }, [originalOrders])

  const goToNextOrder = () => {
    if (orders.length > 1) {
      const index = orders.findIndex(({id}) => id === orderId)
      const updatedOrders = [...orders.slice(0, index), ...orders.slice(index + 1)]
      setOrders(updatedOrders)
      setOrderIndex(1)
      setOrderId(updatedOrders[0].id)
    } else {
      refetch()
    }
  }

  const handleApprove = () => {
    triggerSuccessToast(formatMessage({id: "admin.components.orderManagement.view.toastTitle1"}))
    goToNextOrder()
  }

  const handleReject = () => {
    triggerSuccessToast(formatMessage({id: "admin.components.orderManagement.view.toastTitle2"}))
    goToNextOrder()
  }

  const handleMinimumWageCalculation = () => {
    setShowSuccessAlert(false)
    setShowErrorAlert(false)
    const wage = paidBy === PAID_BY_ANNUAL_SALARY ? monthlyEquivalentHourlyRate : hourlyquivalentHourlyRate
    if (wage >= minimumWage) {
      setShowSuccessAlert(true)
    } else {
      setShowErrorAlert(true)
    }
  }

  const updateEmployeeProfile = () => {
    adminTracking.orderManagementUpdateEmployee(order_number, employee_number, ni_number, apply_updates_to_employee_profile)
    mutate({
      variables: {
        order_id: order_number,
        employee_number,
        ni_number,
        apply_updates_to_employee_profile
      }
    })
      .then(() => {
        triggerSuccessToast(formatMessage({id: "admin.components.orderManagement.view.toastTitle3"}))
      })
      .catch(() => {
        triggerErrorToast(formatMessage({id: "admin.components.orderManagement.view.toastTitle4"}))
      })
  }

  const updateBasicCheckCompleted = () => {
    const basicCheck = basic_details_check_complete
    setBasicDetailsToggled(!basicCheck)
    mutate({
      variables: {
        order_id: order_number,
        basic_details_check_complete: !basicCheck
      }
    }).catch(() => {
      triggerErrorToast(formatMessage({id: "admin.components.orderManagement.view.toastTitle5"}))
      setBasicDetailsToggled(basicCheck)
    })
  }

  const updateOrderCheckCompleted = () => {
    const orderDetailsCheck = order_details_check_complete
    setOrderDetailsToggled(!orderDetailsCheck)
    mutate({
      variables: {
        order_id: order_number,
        order_details_check_complete: !orderDetailsCheck
      }
    }).catch(() => {
      triggerErrorToast(formatMessage({id: "admin.components.orderManagement.view.toastTitle5"}))
      setOrderDetailsToggled(orderDetailsCheck)
    })
  }

  const updateLimitationCheckCompleted = () => {
    const currentWageCheck = national_minimum_wage_check_complete
    setLimitationToggled(!currentWageCheck)
    mutate({
      variables: {
        order_id: order_number,
        national_minimum_wage_check_complete: !currentWageCheck
      }
    }).catch(() => {
      triggerErrorToast(formatMessage({id: "admin.components.orderManagement.view.toastTitle5"}))
      setLimitationToggled(currentWageCheck)
    })
  }

  const handleNextOrder = () => {
    if (orderIndex <= orders.length - 1) {
      setOrderIndex(orderIndex + 1)
      const updatedOrderId = orders[orderIndex].id
      setOrderId(updatedOrderId)
      adminTracking.orderManagementNextOrder(updatedOrderId)
    }
  }

  const handlePreviousOrder = () => {
    if (orderIndex > 1) {
      setOrderIndex(orderIndex - 1)
      const updatedOrderId = orders[orderIndex - 2].id
      setOrderId(updatedOrderId)
      adminTracking.orderManagementPreviousOrder(updatedOrderId)
    }
  }

  return (
    <Loading load={load}>
      <ToastAlert />
      <Flex justifyContent="space-between">
        <BackLink onClick={onBack} />
        <NavigateThroughOrders>
          <IconButton icon={IconTypes.arrowLeft} marginRight onClick={handlePreviousOrder} />
          <FormattedMessage id="admin.components.orderManagement.view.title" values={{order: orderIndex, orders: orders.length}} />
          <IconButton icon={IconTypes.arrowRight} marginLeft onClick={handleNextOrder} />
        </NavigateThroughOrders>
      </Flex>
      <Flex>
        <FlexChild>
          <ProfilePicAndName
            firstName={data?.first_name}
            lastName={data?.surname}
            employeeNumber={`${data?.employee_id} - ${data?.ni_number}`}
          />
          <Flex marginTop={2} marginBottom={2} gap={4}>
            <div>
              <Label>
                <FormattedMessage id="admin.components.orderManagement.view.approval" />
              </Label>
              <StatusPill status={data?.approval_status} />
            </div>
            <div>
              <Label>
                <FormattedMessage id="admin.components.orderManagement.view.status" />
              </Label>
              <StatusPill status={data?.order_status} />
            </div>
            <div>
              <Label>
                <FormattedMessage id="admin.components.orderManagement.view.orderDate" />
              </Label>
              <Body small>{moment(data?.effective_date).format("DD MMM YYYY")}</Body>
            </div>
            <div>
              <Label>
                <FormattedMessage id="admin.components.orderManagement.view.orderNr" />
              </Label>
              <Body small>{data?.order_number}</Body>
            </div>
            <div>
              <Label>
                <FormattedMessage id="admin.components.orderManagement.view.benefitType" />
              </Label>
              <Body small>{data?.benefit_type}</Body>
            </div>
          </Flex>
          {!readOnly && <ApproveRejectButtonModals onApprove={handleApprove} orderId={order_number} onReject={handleReject} />}
          <ViewOrderExpander
            text="admin.components.orderManagement.view.basicDetailsCheck"
            readOnly={readOnly}
            isChecked={basic_details_check_complete}
            setChecked={updateBasicCheckCompleted}>
            <ViewOrderRow text="admin.components.orderManagement.list.header.name">
              <Body small>{`${data?.first_name} ${data?.surname}`}</Body>
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.list.label.employeeNr">
              {readOnly ? (
                <Body small>{data?.employee_id}</Body>
              ) : (
                <Row>
                  <Input
                    label=""
                    value={employee_number}
                    onChange={setEmployeeNumber}
                    validation={validation.string("platform.defaultValidation.required")}
                  />
                </Row>
              )}
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.list.label.niNr">
              {readOnly ? (
                <Body small>{data?.ni_number}</Body>
              ) : (
                <Row>
                  <Input
                    label=""
                    value={ni_number}
                    onChange={setNINumber}
                    validation={validation.string("platform.defaultValidation.required")}
                  />
                </Row>
              )}
              {!readOnly && (
                <>
                  <Flex justifyContent="flex-end">
                    <Checkbox
                      name="orderManagementCheckbox"
                      checked={apply_updates_to_employee_profile}
                      onChange={() => {
                        setApplyUpdates(!apply_updates_to_employee_profile)
                      }}>
                      <FormattedMessage id="admin.components.orderManagement.view.checkbox" />{" "}
                      <IconTooltip>
                        <FormattedMessage id="admin.components.orderManagement.view.checkboxTooltip" />
                      </IconTooltip>
                    </Checkbox>
                  </Flex>
                  <Flex justifyContent="flex-end">
                    <Button onClick={updateEmployeeProfile}>
                      <FormattedMessage id="admin.components.orderManagement.view.updateEmployee" />
                    </Button>
                  </Flex>
                </>
              )}
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.dateOfBirth">
              <Body small>{moment(data?.date_of_birth).format("DD MMM YYYY")}</Body>
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.address">
              <Body small>
                {data?.address1}, {data?.address2}, {data?.city}, {data?.county}, {data?.postcode}
              </Body>
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.telephoneNr">
              <Body small>{data?.telephone_number}</Body>
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.mobileNr">
              <Body small>{data?.mobile_number}</Body>
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.email">
              <Body small>{data?.email}</Body>
            </ViewOrderRow>
          </ViewOrderExpander>
          <ViewOrderExpander
            text="admin.components.orderManagement.view.orderDetailsCheck"
            readOnly={readOnly}
            isChecked={order_details_check_complete}
            setChecked={updateOrderCheckCompleted}>
            <ViewOrderRow text="admin.components.orderManagement.view.orderId">
              <Body small>{data?.id}</Body>
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.schemeName">
              <Body small>{data?.benefit_name}</Body>
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.schemeType">
              <Body small>{data?.benefit_type}</Body>
            </ViewOrderRow>
            <ViewOrderRow
              text={
                data?.products?.length > 1
                  ? "admin.components.orderManagement.view.products"
                  : "admin.components.orderManagement.view.product"
              }>
              {data?.products.map((product) => <Body small>{product.item_name}</Body>)}
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.monthlySalary">
              <Body small>
                <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={data?.amount_per_pay_period} isCents={false} />
              </Body>
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.numberPayments">
              <Body small>{data?.duration}</Body>
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.totalOrder">
              <Body small>
                <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={data?.total_amount_to_recover} isCents={false} />
              </Body>
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.payFrequency">
              <Body small>{data?.pay_frequency}</Body>
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.totalVAT">
              <Body small>
                <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={data?.total_end_of_agreement_inc_vat} isCents={false} />
              </Body>
            </ViewOrderRow>
          </ViewOrderExpander>
          {data?.benefit_type === "Annual Leave Purchase" && (
            <ViewOrderExpander text="admin.components.orderManagement.view.lineManager">
              <ViewOrderRow text="admin.components.orderManagement.view.lineManagerName">
                <Body small>{data.line_manager_name}</Body>
              </ViewOrderRow>
              <ViewOrderRow text="admin.components.orderManagement.view.lineManagerEmail">
                <Body small>{data.line_manager_email}</Body>
              </ViewOrderRow>
            </ViewOrderExpander>
          )}
          <ViewOrderExpander
            text="admin.components.orderManagement.view.limitationCalculator"
            readOnly={readOnly}
            isChecked={national_minimum_wage_check_complete}
            setChecked={updateLimitationCheckCompleted}>
            <ViewOrderRow text="admin.components.orderManagement.view.paymentType">
              {readOnly ? (
                <Body small>
                  <FormattedMessage id="admin.components.orderManagement.view.annualSalaryPaid" />
                </Body>
              ) : (
                <Row>
                  <Select
                    options={[
                      {label: formatMessage({id: "admin.components.orderManagement.view.annualSalaryPaid"}), value: PAID_BY_ANNUAL_SALARY},
                      {label: formatMessage({id: "admin.components.orderManagement.view.hourlyRatePaid"}), value: PAID_BY_HOURLY_SALARY}
                    ]}
                    onChange={setPaidBy}
                    value={paidBy}
                  />
                </Row>
              )}
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.salaryType">
              {readOnly ? (
                <Body small>£0.00</Body>
              ) : (
                <Row>
                  <Input type="number" label="" value={salary} onChange={setSalary} />
                </Row>
              )}
            </ViewOrderRow>
            <ViewOrderRow text="admin.components.orderManagement.view.hoursPerWeek">
              {readOnly ? (
                <Body small>{"0"}</Body>
              ) : (
                <Row>
                  <Input type="number" label="" value={hours} onChange={setHours} />
                </Row>
              )}
            </ViewOrderRow>
            <ViewOrderRow
              text="admin.components.orderManagement.view.totalMonthly.title"
              tooltip="admin.components.orderManagement.view.totalMonthly.tooltip">
              <Body small>
                <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={data?.amount_per_pay_period} isCents={false} />
              </Body>
            </ViewOrderRow>
            <ViewOrderRow
              text="admin.components.orderManagement.view.currentMonthly.text"
              tooltip="admin.components.orderManagement.view.currentMonthly.tooltip">
              <Body small>
                <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={data?.amount_existing_orders} isCents={false} />
              </Body>
            </ViewOrderRow>
            <ViewOrderRow
              text="admin.components.orderManagement.view.totalCost.text"
              tooltip="admin.components.orderManagement.view.totalCost.tooltip">
              <Body small>
                <FormattedCurrency
                  currency={PaymentCurrencyType.Gbp}
                  value={data?.amount_existing_orders * data?.duration}
                  isCents={false}
                />
              </Body>
            </ViewOrderRow>
            <ViewOrderRow
              text="admin.components.orderManagement.view.otherSalary.text"
              tooltip="admin.components.orderManagement.view.otherSalary.tooltip">
              {readOnly ? (
                <Body small>{"0"}</Body>
              ) : (
                <Row>
                  <Input type="number" label="" value={otherDeductions} onChange={setOtherDeductions} />
                </Row>
              )}
            </ViewOrderRow>
            <Hr />
            <H3 marginBottom={2}>
              <FormattedMessage id="admin.components.orderManagement.view.results" />
            </H3>
            <Flex>
              <FlexChild>
                <Spacing marginTop={5} />
                <Body small marginBottom={2}>
                  <FormattedMessage id="admin.components.orderManagement.view.monthlyGrossPay" />
                </Body>
                <Body small marginBottom={2}>
                  <FormattedMessage id="admin.components.orderManagement.view.hourlyRate" />
                </Body>
                <Body small marginBottom={2}>
                  <FormattedMessage id="admin.components.orderManagement.view.nationalMinimum" />
                </Body>
              </FlexChild>
              <FlexChild>
                <Flex>
                  <FlexChild>
                    <Body small marginBottom={2}>
                      <FormattedMessage id="admin.components.orderManagement.view.basic" />
                    </Body>
                    <Body small marginBottom={2}>
                      {readOnly ? (
                        "-"
                      ) : paidBy === PAID_BY_ANNUAL_SALARY ? (
                        <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={salary / 12} isCents={false} />
                      ) : (
                        <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={hourlyAnnualSalary / 12} isCents={false} />
                      )}
                    </Body>
                    <Body small marginBottom={2}>
                      {readOnly ? (
                        "-"
                      ) : paidBy === PAID_BY_ANNUAL_SALARY ? (
                        <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={salary / 52.143 / hours} isCents={false} />
                      ) : (
                        <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={salary} isCents={false} />
                      )}
                    </Body>
                    <Body small marginBottom={2}>
                      -
                    </Body>
                  </FlexChild>
                  <FlexChild>
                    <Body small marginBottom={2}>
                      <FormattedMessage id="admin.components.orderManagement.view.revised" />
                    </Body>
                    <Body small marginBottom={2}>
                      {readOnly ? (
                        "-"
                      ) : paidBy === PAID_BY_ANNUAL_SALARY ? (
                        <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={monthlyGrossPayRevised} isCents={false} />
                      ) : (
                        <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={hourlyGrossPayRevised} isCents={false} />
                      )}
                    </Body>
                    <Body small marginBottom={2}>
                      {readOnly ? (
                        "-"
                      ) : paidBy === PAID_BY_ANNUAL_SALARY ? (
                        <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={monthlyEquivalentHourlyRate} isCents={false} />
                      ) : (
                        <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={hourlyquivalentHourlyRate} isCents={false} />
                      )}
                    </Body>
                    <Body small marginBottom={2}>
                      <FormattedCurrency currency={PaymentCurrencyType.Gbp} value={minimumWage} isCents={false} />
                    </Body>
                  </FlexChild>
                </Flex>
              </FlexChild>
            </Flex>
            {showSuccessAlert && (
              <Alert type={AlertTypes.success} title="admin.components.orderManagement.view.alertTitle1" nonDismissable />
            )}
            {showErrorAlert && <Alert type={AlertTypes.error} title="admin.components.orderManagement.view.alertTitle2" nonDismissable />}
            {!readOnly && (
              <Button full marginTop={2} marginBottom={2} onClick={handleMinimumWageCalculation}>
                <FormattedMessage id="admin.components.orderManagement.view.calculateNationalWage" />
              </Button>
            )}
          </ViewOrderExpander>
          {!readOnly && <ApproveRejectButtonModals onApprove={handleApprove} orderId={order_number} onReject={handleReject} />}
        </FlexChild>
        <CommentSection>
          <Comments comments={data?.comments} orderId={order_number} onSuccess={refetch} />
          <OtherOrders currentOrder={data?.order_number} employeeNumber={data?.employee_id} orders={orders} />
          <Documents order_id={data?.order_number} />
        </CommentSection>
        <ApproveOrderModal showModal={showApproveModal} onClose={() => setShowApproveModal(false)} />
      </Flex>
    </Loading>
  )
}

export default OrderManagementView
